import { Box, Button, Typography } from '@mui/material'
import { uploadData } from 'aws-amplify/storage'
import { useContext } from 'react'

import { Modal } from '../../../components/Modal/Modal'
import {
  ResourcesContext,
  ResourcesContextInterface,
} from '../../../providers/Resources/Resources.provider'
import { ResourceFile, ResourceFolder } from './Resources.view'

interface UploadResourceModalInterface {
  currentFolder: ResourceFolder | null
  uploadModalOpen: boolean
  setUploadModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  uploadInProgress: boolean
  setUploadInProgress: React.Dispatch<React.SetStateAction<boolean>>
  fetchFolderContent: (folderId: number) => Promise<void>
  fileEdit: ResourceFile | null
  setFileEdit: React.Dispatch<React.SetStateAction<ResourceFile | null>>
}

export const UploadResourceModal = ({
  currentFolder,
  uploadModalOpen,
  setUploadModalOpen,
  uploadInProgress,
  setUploadInProgress,
  fetchFolderContent,
  fileEdit,
  setFileEdit,
}: UploadResourceModalInterface) => {
  const { createResourceFile, updateResourceFile } = useContext(
    ResourcesContext
  ) as ResourcesContextInterface

  // Handle file upload / edit file
  const handleFileUploadOrEdit = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]
      setUploadInProgress(true)
      try {
        // Upload to S3
        const fileName = `resource/${Date.now()}_${file.name}`

        const titleName = file.name.replace(/\.[^/.]+$/, '')

        const operation = uploadData({
          path: `public/${fileName}`,
          data: file,
          options: {
            contentType: file.type,
            contentDisposition: `inline; filename="${file.name}"`,
            metadata: {
              title: titleName,
              originalName: file.name,
              uploadDate: new Date().toISOString(),
            },
          },
        })
        const result = await operation.result
        const fileStoragePath = result.path.replace(/^public\//, '')

        if (currentFolder && currentFolder.id) {
          // update file
          if (fileEdit) {
            const updaterRequest = {
              fileId: fileEdit.id,
              folderId: fileEdit.folderId,
              newFileName: file.name,
              newStorageKey: fileStoragePath,
            }
            await updateResourceFile(updaterRequest)
            setFileEdit(null)
          } else {
            // create file
            await createResourceFile(
              file.name,
              currentFolder.id,
              fileStoragePath
            )
          }
          fetchFolderContent(currentFolder.id)
        }

        setUploadModalOpen(false)
        alert(`File ${file.name} uploaded successfully!`)
      } catch (error) {
        console.error('Error uploading file:', error)
        alert('Error uploading file. Please try again.')
      } finally {
        setUploadInProgress(false)
      }
    }
  }

  return (
    <Modal
      title={fileEdit ? 'Update File' : 'Upload Resource'}
      open={uploadModalOpen}
      setOpen={setUploadModalOpen}
      ModalContent={
        <Box sx={{ p: 2 }}>
          <Typography style={{ justifySelf: 'center', marginBottom: '10px' }}>
            {fileEdit
              ? 'Please select a new PDF file to replace the current one'
              : 'Please select a PDF file to upload'}
          </Typography>
          <Box style={{ justifySelf: 'center' }}>
            <input
              accept='application/pdf'
              style={{ display: 'none' }}
              id='contained-button-file'
              type='file'
              onChange={handleFileUploadOrEdit}
              disabled={uploadInProgress}
            />
            <label htmlFor='contained-button-file'>
              <Button
                variant='contained'
                component='span'
                style={{ width: '200px', marginTop: 3 }}
                disabled={uploadInProgress}
              >
                {uploadInProgress ? 'Uploading...' : 'Select File'}
              </Button>
            </label>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => setUploadModalOpen(false)}
              disabled={uploadInProgress}
            >
              <Typography>Cancel</Typography>
            </Button>
          </Box>
        </Box>
      }
    />
  )
}
